import React from 'react';
import Chip from '../../shared/Chip';
import Icon from '../../shared/Icon';
import Timestamp from '../../shared/Timestamp';
import { initialsOf, initialsStyle } from '../../Account/avatar';
import { SHARE_EVENT_TEXT } from '../sharedQueries';

// Shared Queries handoff: the pieces a shared query adds to the iteration 1 thread.

// "A", "A and B", "A, B and C".
export const listJoin = (names) => (names.length < 2
  ? names.join('')
  : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

/** Avatar, size S. A photo when there is one, otherwise initials (the API sends no asker photos yet). */
export function PersonAvatar({ name, photoUrl, color = '', named = false }) {
  return (
    <span className="iter1-avatar" data-tip={named ? name : undefined} aria-hidden="true">
      {photoUrl
        ? <img src={photoUrl} alt="" className="iter1-avatar-photo" />
        : <span className="iter1-avatar-initials" style={initialsStyle(name, color)}>{initialsOf(name)}</span>}
    </span>
  );
}

/**
 * Avatar Stack (Figma 1411:126): everyone who has asked, in order of their first question. One
 * tab stop named "Asked by {names}", whose tooltip names everyone.
 */
export function AvatarStack({ people }) {
  if (!people.length) return null;
  const names = people.map((p) => p.name);
  return (
    <span className="iter1-avatar-stack" role="group" tabIndex={0} aria-label={`Asked by ${listJoin(names)}`} data-tip={names.join('\n')}>
      {people.map((p) => <PersonAvatar key={p.id} name={p.name} photoUrl={p.photoUrl} color={p.color} named />)}
    </span>
  );
}

/** The header's shared line: the Avatar Stack, then "Shared by {owner}" or "Shared by you". */
export function SharedLine({ people, ownerLabel }) {
  return (
    <p className="iter1-thread-meta">
      <AvatarStack people={people} />
      <span>Shared by {ownerLabel}</span>
    </p>
  );
}

/**
 * Share Event Divider (Figma 1411:148): where the query was shared, stopped being shared or
 * shared again, with its date. A quiet divider between hairline rules; it names no one.
 */
export function ShareEventDivider({ event, at }) {
  return (
    <p className="iter1-share-divider" data-testid="iter1-share-divider">
      <span className="iter1-share-divider-text">
        <Icon name={event === 'stopped' ? 'user-minus' : 'users'} size={16} className="iter1-share-divider-glyph" />
        <span>{SHARE_EVENT_TEXT[event]} · <Timestamp value={at} form="long" /></span>
      </span>
    </p>
  );
}

/** "For {name}" on an answer's ResAI strip; focusable so keyboard users get its tooltip. */
export function ForChip({ name, you }) {
  return (
    <Chip className="iter1-for-chip" tabIndex={0} data-tip={you ? 'Answered with your access' : `Answered with ${name}’s access`}>
      {you ? 'For you' : `For ${name}`}
    </Chip>
  );
}

/**
 * Icon trigger - small, State=Disabled (Figma 1410:3221): no action, opacity-60, still focusable so
 * its tooltip ("{label} · {note}") can say why.
 */
export function DisabledAction({ icon, label, note }) {
  return (
    <button type="button" className="iter1-icon-btn" aria-label={label} aria-disabled="true" data-tip={label} data-tip-note={note}>
      <Icon name={icon} size={16} />
    </button>
  );
}

/** Reasoning Card, Variant=Withheld (Figma 1410:3224): a glyph and a sentence in place of content. */
export function WithheldCard({ icon, children, className = '' }) {
  return (
    <p className={`iter1-withheld ${className}`.trim()}>
      <Icon name={icon} size={22} className="iter1-withheld-glyph" />
      <span>{children}</span>
    </p>
  );
}

export const WITHHELD_ANSWER_TEXT = 'This answer isn’t available to you because it uses data you don’t have access to. '
  + 'Contact your administrator to request access.';
export const READ_ONLY_TEXT = 'You can read this query but can’t ask in it.';
export const AUDIENCE_TEXT = 'Everyone in your organization can see your question and its answer';

/** The composer's audience line, linked to the textarea with aria-describedby. */
export function AudienceNote({ id }) {
  return (
    <p id={id} className="iter1-composer-note">
      <Icon name="users" size={16} className="iter1-composer-note-glyph" />
      <span>{AUDIENCE_TEXT}</span>
    </p>
  );
}

/** Button - Tertiary - Extra Small over the thread, gap-2 above the footer. */
export function NewActivityButton({ onClick }) {
  return (
    <button type="button" className="iter1-xs-btn iter1-live-jump" onClick={onClick}>
      <Icon name="arrow-down" size={16} />
      <span>New activity</span>
    </button>
  );
}
